//deps
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import { CartCutleryInstructionProps } from "@swiggy-private/cart-ui";
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// hooks
import { useCartRendererAnalytics } from "./useCartRenderedAnalytics";

// constants
import { CUTLEY_DATA, DONT_SEND_CUTLERY } from "@cart-client/constants/TextDict";
import { CartActionTypes } from "@cart-client/reducers/CartReducer";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// analytics
import { clickCutleryChecked, clickCutleryUnChecked } from "@cart-client/analytics/events";

// helpers
import { canShowCutleryInstruction, getDeliveryInstructionData } from "@cart-client/helpers/Cart";
import { isTipDetailsExist } from "@cart-client/helpers/DeTip";

export interface UseCutleryInstructionDataReturnType {
    cutleryWidgetData: CartCutleryInstructionProps;
    showCutleryInstruction: boolean;
}

export const useCutleryInstructionData = (): UseCutleryInstructionDataReturnType => {
    const { cartState, setCartState } = useContext(CartContext);
    const { clickEvent } = useContext(AnalyticsContext);
    const { reviewedCart: cartResponse, localCart } = cartState;
    const { onImpressionCutleryShown, onImpressionCutleryNotShown } = useCartRendererAnalytics(cartResponse);

    const showCutleryInstruction = useMemo(() => {
        const isDeTipDetailsExist = isTipDetailsExist(cartResponse?.data);
        const showInstruction = canShowCutleryInstruction(cartResponse?.data);
        const deliveryInstructionArray = getDeliveryInstructionData(cartResponse);

        // This logic is taken from Cart 1.0 Bill details wrapper & Bill details Item. Show cutlery instructions only when detip exist & delivery instructions not exist
        return (
            isDeTipDetailsExist && showInstruction && !(deliveryInstructionArray && deliveryInstructionArray.length > 0)
        );
    }, [cartResponse]);

    const cutleryInfo = useMemo(
        () => ({
            selected: (localCart?.cutleryComments && localCart?.cutleryComments?.length > 0) || false,
            config: {
                title: CUTLEY_DATA.title,
                preSelectionSubtitle: CUTLEY_DATA.preSelectionSubtitle,
                postSelectionSubtitle: CUTLEY_DATA.postSelectionSubtitle,
            },
        }),
        [localCart?.cutleryComments],
    );

    const onCheckChangeCallback = useCallback(
        (checked: boolean) => {
            checked ? clickCutleryChecked(clickEvent) : clickCutleryUnChecked(clickEvent);
            setCartState({
                type: CartActionTypes.UPDATE_LOCAL_CART,
                payload: {
                    ...localCart,
                    cutleryComments: checked ? DONT_SEND_CUTLERY : "",
                },
            });
        },
        [localCart, setCartState, clickEvent],
    );

    return {
        cutleryWidgetData: {
            ...cutleryInfo,
            onCheckChangeCallback,
            onImpressionCutleryNotShown,
            onImpressionCutleryShown,
        },
        showCutleryInstruction,
    };
};
